import { Typography } from '@startuptools/ui/Typography';
import { Card } from '@startuptools/ui/Card';
import { DateInput } from '@startuptools/ui/DateInput';
import { Trans, useLingui } from '@lingui/react';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { DateTime } from 'luxon';
import { Control, Controller, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Stack } from '@startuptools/ui/Stack';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { GqlCompany, GqlPaymentAccountKind } from '../../../graphql/base-types.graphql';
import { Autocomplete } from '@startuptools/ui/Autocomplete';
import { ReactNode, useEffect, useState } from 'react';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { BankGiro, getBankgiro } from '../../../injectables/bankgiro.service';
import { AutocompleteOption } from '@startuptools/ui/AutocompleteOption';
import { ListItemContent } from '@startuptools/ui/ListItemContent';
import { isString } from 'lodash-es';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Input } from '@startuptools/ui/Input';
import { bankCode } from '@startuptools/common/common';
import { Alert } from '@startuptools/ui/Alert';
import { useStepperStepContent } from '../../../components/steppers/common/StepperStepContentContext';
import { ErrorDisplay } from '../../../components/error-display/ErrorDisplay';

export type FormValues = {
  payBeforeDate: DateTime;
  paymentAccountKind: GqlPaymentAccountKind;
  paymentAccountNumber: string;
  paymentName: string;
  ibanNumber: string;
  ibanBic: string;
  ibanBank: string;
  ibanAddress: string;
};

type MinDateError = {
  date: DateTime;
  severity: 'error';
};

type MinDateWarning = {
  date: DateTime;
  severity: 'warning';
  warningText: ReactNode;
};

export const StepPayment = ({
  control,
  register,
  watch,
  setValue,
  company,
  minDate,
}: {
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  company: Pick<GqlCompany, 'organizationNumber'>;
  minDate: MinDateError | MinDateWarning;
}) => {
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [bankgiroOptions, setBankgiroOptions] = useState<BankGiro[]>([]);
  const [bankgiroLoading, setBankgiroLoading] = useState(false);
  const { i18n } = useLingui();
  const { validatorStatus } = useStepperStepContent();

  const paymentAccountKind = watch('paymentAccountKind');
  const paymentAccountNumber = watch('paymentAccountNumber');
  const ibanNumber = watch('ibanNumber');

  useEffect(() => {
    if (paymentAccountKind !== GqlPaymentAccountKind.Bankgiro) {
      setBankgiroOptions([]);
      return;
    }
    if (paymentAccountNumber) {
      setOpenAutocomplete(false);
      return;
    }
    const doSearch = async (str: string) => {
      const res = await getBankgiro(str);
      setBankgiroOptions(res);
      setOpenAutocomplete(true);
    };
    setBankgiroLoading(true);
    void doSearch(company.organizationNumber).finally(() => setBankgiroLoading(false));
  }, [company.organizationNumber, paymentAccountKind, paymentAccountNumber]);

  return (
    <>
      <Controller
        control={control}
        name="payBeforeDate"
        rules={{
          validate: v => {
            if (minDate.severity === 'warning') {
              return true;
            }
            if (v && v.toMillis() < minDate.date.toMillis())
              return i18n._({
                id: 'Payment due date must be after {date}',
                values: { date: minDate.date.toLocaleString() },
              });
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl sx={{ maxWidth: 300 }} error={Boolean(error)}>
            <FormLabel>
              <Trans id="Payment Due Date" />
            </FormLabel>
            <DateInput {...field} minDate={minDate.severity === 'error' ? minDate.date : undefined} />
            {error && <FormHelperText>{error.message}</FormHelperText>}
            {!error &&
              minDate.severity === 'warning' &&
              field.value &&
              field.value.startOf('day').toMillis() < minDate.date.startOf('day').toMillis() && (
                <FormHelperText sx={theme => ({ color: theme.palette.warning.mainChannel })}>
                  {minDate.warningText}
                </FormHelperText>
              )}
          </FormControl>
        )}
      />
      <Card>
        <Stack gap={2}>
          <Typography level="title-lg">
            <Trans id="Account for domestic payment" />
          </Typography>
          <Controller
            control={control}
            name="paymentAccountKind"
            render={({ field: { onChange, ...field } }) => (
              <FormControl sx={{ maxWidth: 400 }}>
                <FormLabel>
                  <Trans id="Payment method" />
                </FormLabel>{' '}
                <Select
                  {...field}
                  onChange={(_, v) => {
                    if (v !== paymentAccountKind) {
                      setValue('paymentAccountNumber', '');
                      setValue('paymentName', '');
                    }
                    onChange(v);
                  }}
                >
                  <Option value={GqlPaymentAccountKind.Other}>
                    <Trans id="To an account specified by the company" />
                  </Option>
                  <Option value={GqlPaymentAccountKind.Bankgiro}>Bankgiro</Option>
                  <Option value={GqlPaymentAccountKind.BankAccount}>
                    <Trans id="Swedish Bank Account" />
                  </Option>
                </Select>
              </FormControl>
            )}
          />

          {paymentAccountKind === GqlPaymentAccountKind.Bankgiro && (
            <>
              <Controller
                control={control}
                rules={{
                  required: true,
                  validate: v => {
                    if (v && /^\d{3,4}-?\d{4}/.test(v)) return true;
                    return i18n._({ id: 'Invalid Bankgiro number' });
                  },
                }}
                name="paymentAccountNumber"
                render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                  <FormControl sx={{ maxWidth: 400 }} required error={Boolean(error)}>
                    <FormLabel>
                      <Trans id="Bankgironummer" />
                    </FormLabel>
                    <Autocomplete
                      {...field}
                      inputValue={value ?? ''}
                      onInputChange={(_, v) => onChange(v)}
                      onChange={(_, v) => (isString(v) || !v ? null : setValue('paymentName', v.name))}
                      open={openAutocomplete}
                      onOpen={() => setOpenAutocomplete(true)}
                      onClose={() => setOpenAutocomplete(false)}
                      options={bankgiroOptions}
                      getOptionLabel={o => (isString(o) ? o : o.bgnr)}
                      filterOptions={o => o}
                      renderOption={(props, o) => (
                        <AutocompleteOption {...props}>
                          <ListItemContent>
                            <Typography level="title-lg">{o.bgnr}</Typography>
                            <Typography level="body-sm" noWrap>
                              <Trans id="Company: {name} - {orgNr}" values={{ name: o.name, orgNr: o.orgnr }} />
                            </Typography>
                          </ListItemContent>
                        </AutocompleteOption>
                      )}
                      noOptionsText={i18n._({ id: 'Input an org number or bankgironumber' })}
                      loading={bankgiroLoading}
                      endDecorator={bankgiroLoading ? <CircularProgress size="sm" /> : null}
                      freeSolo
                    />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />

              <FormControl required sx={{ maxWidth: 400 }}>
                <FormLabel>
                  <Trans id="Name of Recipient" />
                </FormLabel>

                <Input {...register('paymentName')} required />
              </FormControl>
            </>
          )}

          {paymentAccountKind === GqlPaymentAccountKind.BankAccount && (
            <>
              <Controller
                control={control}
                name="paymentAccountNumber"
                render={({ field: { onChange, ...field } }) => (
                  <FormControl required sx={{ maxWidth: 400 }}>
                    <FormLabel>
                      <Trans id="Account Number (incl. clearing)" />
                    </FormLabel>

                    <Input
                      {...field}
                      onChange={v => {
                        const cNumber = v.target.value.replace(/\D+/g, '').substring(0, 4);
                        const name = bankCode(cNumber)?.name;
                        if (name) {
                          setValue('paymentName', name);
                        }
                        onChange(v);
                      }}
                      required
                    />
                  </FormControl>
                )}
              />

              <FormControl required sx={{ maxWidth: 400 }}>
                <FormLabel>
                  <Trans id="Bank" />
                </FormLabel>

                <Input {...register('paymentName')} required />
              </FormControl>
            </>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack gap={2}>
          <Typography level="title-lg">
            <Trans id="Account for foreign payment (optional)" />
          </Typography>

          <Alert>
            <Typography component="span" level="body-sm">
              <Trans
                id="
            When a payment is made from another country, it is especially important that the paid amount is correct. If too little money is received, the issue may not be possible to register, since the bank certificate will be for an amount which is lower than in the decision. In order to prevent that, it is important that:

            <ol>
              <li>The investor must pay all fees in connection with the transfer. If fees are drawn for the receiver, too little money will be reiceved.</li>
              <li>The payment is made in the correct currency, which is usually SEK (however, Swedish companies may issue shares in another currency). If the transfer is made in a different currency, there is a risk that the currency conversion will yield a different amount than what is required.</li>
            </ol>

            If the payee is located within the EU, it is usually simplest to make a SEPA-payment. This is the fastest form of payment and is often free from fees.
          "
                components={{ ol: <ol />, li: <li /> }}
              />
            </Typography>
          </Alert>
          <FormControl sx={{ maxWidth: 400 }}>
            <FormLabel>IBAN</FormLabel>
            <Input {...register('ibanNumber')} />
          </FormControl>
          <FormControl sx={{ maxWidth: 400 }} required={Boolean(ibanNumber)}>
            <FormLabel>Bank</FormLabel>
            <Input {...register('ibanBank')} required={Boolean(ibanNumber)} />
          </FormControl>
          <FormControl sx={{ maxWidth: 400 }} required={Boolean(ibanNumber)}>
            <FormLabel>BIC</FormLabel>
            <Input {...register('ibanBic')} required={Boolean(ibanNumber)} />
          </FormControl>
          <FormControl sx={{ maxWidth: 400 }} required={Boolean(ibanNumber)}>
            <FormLabel>
              <Trans id="Address" />
            </FormLabel>
            <Input {...register('ibanAddress')} required={Boolean(ibanNumber)} />
          </FormControl>
        </Stack>
      </Card>

      {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}
    </>
  );
};
